/**
 * Popup-based OAuth flow against app-api `/auth/start`. Returns the
 * minted JWT once the popup completes the Workfront OAuth handshake
 * and posts back via `window.opener.postMessage`.
 *
 * Why a popup (not a redirect): the SPA is iframe-embedded inside
 * customer Workfront. A same-tab redirect would navigate the iframe's
 * parent (Workfront itself), and a top-frame redirect would lose the
 * iframe context. A popup is a separate browsing context that can
 * safely host the Workfront authorize redirect, then postMessage the
 * result back to the SPA.
 *
 * Must be called from a direct user gesture (button click) — Safari /
 * Firefox popup blockers will reject otherwise.
 */

const POPUP_TIMEOUT_MS = 5 * 60 * 1000; // 5 min — Workfront auth can be slow

interface AuthSuccessMessage {
  type: 'AUTH_SUCCESS';
  token: string;
  /** Opaque refresh handle the SPA stores client-side and sends as
   *  `Authorization: Bearer …` to `/auth/silent-refresh`. Optional so
   *  the type still matches an older app-api that only sent `token`
   *  — current callers should treat its absence as a cookie-only
   *  fallback session. */
  refreshHandle?: string;
}

interface AuthErrorMessage {
  type: 'AUTH_ERROR';
  error: string;
}

type AuthMessage = AuthSuccessMessage | AuthErrorMessage;

function isAuthMessage(value: unknown): value is AuthMessage {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  if (v.type === 'AUTH_SUCCESS' && typeof v.token === 'string') {
    return v.refreshHandle === undefined || typeof v.refreshHandle === 'string';
  }
  return v.type === 'AUTH_ERROR' && typeof v.error === 'string';
}

export interface PopupAuthResult {
  token: string;
  /** Present when the app-api emits the post-CHIPS message shape; absent for
   *  older deployments. Caller should still treat presence as authoritative. */
  refreshHandle?: string;
}

interface StartPopupAuthArgs {
  /** App-API base URL. The popup target is `${baseUrl}/auth/start?...`. */
  baseUrl: string;
  /** License the customer is authenticating against. */
  licenseId: string;
}

export async function startPopupAuth(args: StartPopupAuthArgs): Promise<PopupAuthResult> {
  const { baseUrl, licenseId } = args;
  // Pass `window.location.origin` so the auth-complete callback can
  // post the token back to whichever origin actually opened the popup.
  // The app-api validates this against `WF_COE_APP_SPA_ALLOWED_ORIGINS`
  // before signing it into the OAuth state, so the SPA can't request
  // delivery to an arbitrary third party. Without this, a SPA running
  // on a non-canonical origin (e.g. `http://localhost:5174` during dev,
  // or any future per-tenant subdomain) would never receive the
  // postMessage — auth-callback would post to the canonical deployed
  // SPA origin which doesn't match the receiving window.
  const params = new URLSearchParams({
    licenseId,
    redirect_origin: window.location.origin,
  });
  const url = `${baseUrl.replace(/\/+$/, '')}/auth/start?${params.toString()}`;
  const popup = window.open(url, 'wf-coe-app-auth', 'width=600,height=700,popup=yes');
  if (!popup) {
    throw new Error('Popup blocked. Allow popups for this site to sign in.');
  }

  const expectedOrigin = new URL(baseUrl).origin;

  return new Promise<PopupAuthResult>((resolve, reject) => {
    let settled = false;
    const finish = (
      resolved: { ok: true; result: PopupAuthResult } | { ok: false; error: string },
    ) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      clearInterval(closedCheck);
      clearTimeout(timeout);
      try {
        popup.close();
      } catch {
        /* may already be closed */
      }
      if (resolved.ok) resolve(resolved.result);
      else reject(new Error(resolved.error));
    };

    const onMessage = (event: MessageEvent): void => {
      if (event.origin !== expectedOrigin) return;
      if (!isAuthMessage(event.data)) return;
      if (event.data.type === 'AUTH_SUCCESS') {
        finish({
          ok: true,
          result: { token: event.data.token, refreshHandle: event.data.refreshHandle },
        });
      } else {
        finish({ ok: false, error: event.data.error });
      }
    };

    // Fallback if the popup is closed before posting a message —
    // happens if the user just X's out of the OAuth screen.
    const closedCheck = window.setInterval(() => {
      if (popup.closed) {
        finish({ ok: false, error: 'Sign-in window was closed.' });
      }
    }, 500);

    const timeout = window.setTimeout(() => {
      finish({ ok: false, error: 'Sign-in timed out. Please try again.' });
    }, POPUP_TIMEOUT_MS);

    window.addEventListener('message', onMessage);
  });
}
